'use client';

import React from 'react';
import { useTranslations } from '@/hooks/useTranslations';

type BlogShareButtonsProps = {
  url: string;
  title: string;
};

type ShareNetwork = 'facebook' | 'x' | 'telegram';

const shareButtonClass =
  'inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/10 bg-white/5 text-slate-300 transition hover:border-[#ffb74d]/40 hover:bg-[#ffb74d]/10 hover:text-[#ffb74d] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#ffb74d]/50';

function FacebookIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M24 12.073C24 5.405 18.627 0 12 0S0 5.405 0 12.073C0 18.1 4.388 23.094 10.125 24v-8.437H7.078v-3.49h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.492 0-1.956.926-1.956 1.874v2.25h3.328l-.532 3.49h-2.796V24C19.612 23.094 24 18.1 24 12.073z" />
    </svg>
  );
}

function XIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );
}

function TelegramIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
    </svg>
  );
}

function LinkIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M13.828 10.172a4 4 0 0 0-5.656 0l-4 4a4 4 0 1 0 5.656 5.656l1.102-1.101m-.758-4.899a4 4 0 0 0 5.656 0l4-4a4 4 0 0 0-5.656-5.656l-1.1 1.1" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
  );
}

export function BlogShareButtons({ url, title }: BlogShareButtonsProps) {
  const { t } = useTranslations();
  const [copied, setCopied] = React.useState(false);

  const encodedUrl = encodeURIComponent(url);
  const encodedTitle = encodeURIComponent(title);

  const share = (network: ShareNetwork) => {
    const targets: Record<ShareNetwork, string> = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
      x: `https://x.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`,
      telegram: `https://t.me/share/url?url=${encodedUrl}&text=${encodedTitle}`,
    };
    window.open(targets[network], '_blank', 'noopener,noreferrer,width=600,height=500');
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  return (
    <section
      aria-label={t('blog.share')}
      className="rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-4 md:px-5"
    >
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-xs font-semibold uppercase tracking-widest text-slate-500">
          {t('blog.share')}
        </span>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => share('facebook')}
            className={shareButtonClass}
            aria-label={t('blog.shareOnFacebook')}
            title={t('blog.shareOnFacebook')}
          >
            <FacebookIcon />
          </button>
          <button
            type="button"
            onClick={() => share('x')}
            className={shareButtonClass}
            aria-label={t('blog.shareOnX')}
            title={t('blog.shareOnX')}
          >
            <XIcon />
          </button>
          <button
            type="button"
            onClick={() => share('telegram')}
            className={shareButtonClass}
            aria-label={t('blog.shareOnTelegram')}
            title={t('blog.shareOnTelegram')}
          >
            <TelegramIcon />
          </button>
          <button
            type="button"
            onClick={copy}
            className="inline-flex h-10 items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 text-xs font-medium text-slate-300 transition hover:border-[#ffb74d]/40 hover:bg-[#ffb74d]/10 hover:text-[#ffb74d] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#ffb74d]/50"
            aria-label={t('blog.copyArticleLink')}
            title={copied ? t('blog.linkCopied') : t('blog.copyLink')}
          >
            {copied ? <CheckIcon /> : <LinkIcon />}
            <span>{copied ? t('blog.linkCopied') : t('blog.copyLink')}</span>
          </button>
        </div>
      </div>
    </section>
  );
}
